import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/common";
import { LEANING, LEANING_ORDER, LeaningBadge } from "@/components/Leaning";
import { SuggestSource } from "@/components/SuggestSource";
import { JournalismNote } from "@/components/FactProcess";

const Group = ({ leaning, rows }) => (
  <section className="space-y-2" data-testid={`sources-group-${leaning}`}>
    <h2 className="flex items-center gap-2 text-xl">{LEANING[leaning]?.[0] || "Not rated"} <span className="text-sm font-normal text-slate-400">({rows.length})</span></h2>
    {rows.length === 0 ? <p className="text-sm text-slate-500">No approved sources in this group yet.</p> : (
      <div className="surface divide-y divide-border">{rows.map((s) => (
        <div key={s.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`approved-source-${s.id}`}>
          <div className="min-w-0"><p className="font-medium">{s.name}</p><p className="truncate text-xs text-slate-500">{s.source_type} · {s.country} · {s.domain}</p></div>
          <LeaningBadge leaning={s.leaning} testid={`approved-source-leaning-${s.id}`} />
        </div>
      ))}</div>
    )}
  </section>
);

export default function ApprovedSources() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/approved-sources").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return <p className="text-sm text-slate-500" data-testid="approved-sources-error">The source list couldn't be loaded. Please try again.</p>;
  if (!d) return <Spinner />;
  const by = (l) => d.sources.filter((s) => (s.leaning || "") === l);
  const unrated = d.sources.filter((s) => !LEANING[s.leaning]);
  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="approved-sources-page">
      <Link to="/facts" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="approved-sources-back"><ArrowLeft size={14} />Back to Facts</Link>
      <PageHeader env="facts" title="Our approved sources" subtitle="Every Fact on Middle comes from one of these sources. Each is rated by its political leaning, so you can see where a story comes from." action={<SuggestSource />} />
      <JournalismNote />
      <aside className="flex gap-3 rounded-2xl border border-[hsl(var(--facts))]/30 bg-emerald-50/50 p-4 text-sm text-slate-700" data-testid="approved-sources-explainer">
        <Scale size={18} className="mt-0.5 shrink-0 text-[hsl(var(--facts))]" />
        <div className="space-y-1.5">
          <p><b>Left and Centre-left</b> sources count as the left-leaning side. <b>Centre-right and Right</b> sources count as the right-leaning side. <b>Neutral / official</b> sources are government, statistics, science and weather agencies and international bodies with no political leaning.</p>
          <p>A Fact is only <b>Corroborated</b> or <b>Verified</b> when it is confirmed across sides: by sources on both sides, or with a neutral/official source. If only sources on the same side confirm it, it is labelled <b>One side only</b>.</p>
          <p className="text-xs text-slate-500">Ratings are Middle's own, starting from widely used public media-bias ratings. They describe a source's overall editorial leaning, not every article.</p>
        </div>
      </aside>
      {LEANING_ORDER.map((l) => <Group key={l} leaning={l} rows={by(l)} />)}
      {unrated.length > 0 && <Group leaning="unrated" rows={unrated} />}
    </div>
  );
}
